import { GeneralError } from '@openx/components-core/lib/GeneralError/GeneralError.tsx';
import { EmptyTablePlaceholder } from '@openx/components-core/lib/TablePlaceholder/EmptyTablePlaceholder.tsx';
import { DealsList as DealsListUI } from '@openx/components-deal/lib/DealsList/DealsList.tsx';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { DealsListFiltersValue, DealsTableFetchOptions, OAListDeal } from '@openx/types/deal.ts';
import { CriteriaDimension, SortDirection } from '@openx/types/tableCriteria.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { OPEN_AUDIENCE_ORG_ID } from '@openx/utils/services/config.ts';
import isEqual from 'lodash/isEqual';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { buildDealLandingQueryCriteria } from '../../../configs/deals/utils/dealLandingQueryCriteria.ts';
import { useDealAccountsFetch } from '../../../configs/deals/utils/useDealAccountsFetch.ts';
import { handlePaginationUpdate } from '../../../utils/handlePaginationUpdate.ts';
import { useLastPopulatedPageRedirect } from '../../../utils/useLastPopulatedPageRedirect.ts';
import { type DealsListCriteria, dealsDefaultCriteria } from './dealsCriteria.ts';

export type DealsListProps = {
  criteria: DealsListCriteria;
  setCriteria: Dispatch<SetStateAction<DealsListCriteria>>;
  selectedDeals: OAListDeal[];
  setSelectedDeals: Dispatch<SetStateAction<OAListDeal[]>>;
};

export const DealsList = ({ criteria, setCriteria, selectedDeals, setSelectedDeals }: DealsListProps) => {
  const navigate = useNavigate();

  const { useDealsLandingListFetch, useDealsRowActions, useDealsTableFetch, useFetchDealIdGroupInfo } =
    useDealContext();

  const rowActions = useDealsRowActions();

  const queryCriteria = useMemo(() => buildDealLandingQueryCriteria(criteria), [criteria]);

  const { session } = useAuth();
  const isOpenAudienceOrg = session?.exchange_session.account?.id === OPEN_AUDIENCE_ORG_ID;

  const hasActiveOrganizationFilter = useMemo(() => {
    const selected = parseMultiValueFilter(criteria.filters?.account);

    return selected.length > 0;
  }, [criteria.filters?.account]);

  // Accounts feed the Organization filter, which is only rendered for the OA org; skip for sub-org sessions.
  const {
    accounts,
    accountUidByNetworkId,
    error: accountsError,
    loading: accountsLoading,
    ready: accountsReady,
  } = useDealAccountsFetch({
    skip: !isOpenAudienceOrg,
  });

  const {
    dealsList = [],
    hasMore,
    loading,
    error: landingError,
    resolveLastPopulatedPage,
  } = useDealsLandingListFetch<OAListDeal>(queryCriteria, {
    accountTranslation: accountUidByNetworkId,
    accountTranslationReady: !isOpenAudienceOrg || accountsReady,
  });

  const error = landingError || (hasActiveOrganizationFilter ? accountsError : null);

  const requestedPage = criteria.pagination?.pageNumber ?? 1;

  const redirectToLastDealPage = useCallback(
    (lastPopulatedPage: number, outOfRangePage: number) => {
      setCriteria(previousCriteria => {
        const previousPagination = previousCriteria.pagination ?? dealsDefaultCriteria.pagination;

        if (previousPagination.pageNumber !== outOfRangePage) {
          return previousCriteria;
        }

        return {
          ...previousCriteria,
          pagination: {
            ...previousPagination,
            pageNumber: lastPopulatedPage,
          },
        };
      });
    },
    [setCriteria]
  );

  const updatePaginationNormally = useCallback(() => {
    handlePaginationUpdate({
      defaultPagination: dealsDefaultCriteria.pagination,
      hasMore,
      isLoading: loading,
      objectsLength: dealsList.length,
      setCriteria,
    });
  }, [dealsList.length, hasMore, loading, setCriteria]);

  useLastPopulatedPageRedirect({
    requestedPage,
    loading,
    objectsLength: dealsList.length,
    hasMore,
    resolveLastPopulatedPage,
    onRedirect: redirectToLastDealPage,
    onRedirectNotNeeded: updatePaginationNormally,
    onFailure: updatePaginationNormally,
  });

  const dealIds = useMemo(() => [...new Set(dealsList.map(deal => deal.deal_id ?? ''))], [dealsList]);
  const { dealGroupInfo } = useFetchDealIdGroupInfo({ dealIds });

  const dealIdsToGroup = useMemo(
    () => dealGroupInfo.filter(group => group.count > 1).map(group => group.deal_id),
    [dealGroupInfo]
  );

  const criteriaAccountId = useMemo(() => {
    const selectedAccounts = parseMultiValueFilter(criteria.filters?.account);

    if (selectedAccounts.length !== 1) {
      return undefined;
    }

    return accounts?.find(account => account.name === selectedAccounts[0])?.id;
  }, [accounts, criteria]);

  const { filtersConfig } = useDealsTableFetch<OAListDeal, DealsTableFetchOptions>(accounts, {
    accountsLoading,
    selectedAccountId: criteriaAccountId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: It should be triggered only when dealsList content change
  useEffect(() => {
    setSelectedDeals([]);
  }, [JSON.stringify(dealsList)]);

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (selectedDeals.length > 0) {
        setSelectedDeals([]);
      }

      setCriteria(previousCriteria => {
        const nextCriteriaChange = { ...newCriteria };

        if (
          nextCriteriaChange?.dimension === CriteriaDimension.SORT &&
          nextCriteriaChange.value?.column === previousCriteria.sort?.column &&
          nextCriteriaChange.value?.direction === SortDirection.ASC &&
          nextCriteriaChange.value?.column !== dealsDefaultCriteria.sort?.column
        ) {
          nextCriteriaChange.value = {
            ...nextCriteriaChange.value,
            column: dealsDefaultCriteria.sort?.column,
            direction: dealsDefaultCriteria.sort?.direction,
          };
        }

        let newCriteriaAdjusted = nextCriteriaChange;

        if (
          nextCriteriaChange.dimension === CriteriaDimension.PAGINATION ||
          nextCriteriaChange.dimension === CriteriaDimension.SORT
        ) {
          newCriteriaAdjusted = nextCriteriaChange;
        } else if (nextCriteriaChange.dimension === CriteriaDimension.FILTERS) {
          newCriteriaAdjusted = {
            dimension: CriteriaDimension.FILTERS,
            value: nextCriteriaChange.value,
          };
        } else {
          newCriteriaAdjusted = {
            dimension: CriteriaDimension.FILTERS,
            value: nextCriteriaChange as DealsListFiltersValue,
          };
        }

        const newCriteriaProcessed = applyCriteriaChange(previousCriteria, newCriteriaAdjusted);

        return isEqual(newCriteriaProcessed, previousCriteria) ? previousCriteria : newCriteriaProcessed;
      });
    },
    [selectedDeals.length, setCriteria, setSelectedDeals]
  );

  const handleFiltersChange = useCallback(
    (filters: DealsListFiltersValue) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value: filters,
      });
    },
    [handleCriteriaChange]
  );

  const handleSelectionChange = useCallback(
    (selectedItems, operation) => {
      const selectedDealsNext = applySelectionChange({
        changeItems: selectedItems,
        itemsComparer: isEqual,
        operation,
        previouslySelectedItems: selectedDeals,
      });

      setSelectedDeals(selectedDealsNext);
    },
    [selectedDeals, setSelectedDeals]
  );

  if (error) {
    return <GeneralError onClick={() => navigate(0)} />;
  }

  const isTableEmpty = !loading && !criteria.filters && dealsList.length === 0;

  if (isTableEmpty) {
    return <EmptyTablePlaceholder />;
  }

  return (
    <DealsListUI<OAListDeal>
      loading={loading}
      changeFilters={handleFiltersChange}
      changeSingleCriteriaDimension={handleCriteriaChange}
      criteria={criteria}
      dealsList={dealsList}
      useDealsRowActions={() => rowActions}
      selectedData={selectedDeals}
      filtersConfig={filtersConfig}
      toggleSelect={handleSelectionChange}
      dealIdFilterKey="exchange_deal_id"
      dealIdsToGroup={dealIdsToGroup}
    />
  );
};
